/**
 * ReportIssueDialog Component
 *
 * Accessible confirmation modal for the "Report a problem" flow (US-016).
 * Cloned from DemoLimitPopup (accessible overlay pattern: role=dialog, aria-modal,
 * auto-focus, focus-trap, Escape-to-dismiss) and adapted for confirm/dismiss actions.
 *
 * Implements US-016 AC-4/AC-5
 * Clones: DemoLimitPopup overlay pattern (focus management, keyboard handling, card markup)
 *
 * CCM-03: No coin balances or coin counts are rendered. The only permitted money
 *         reference is the fixed reassurance string per AC-4. No coin data is fetched.
 *
 * Props are fully controlled — no internal data fetching or navigation.
 * Navigation on confirm happens inside useCancelSession.onSuccess (TO-LL-013).
 */

import { useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Props for ReportIssueDialog
 */
export interface ReportIssueDialogProps {
  /** Controls visibility; returns null when false (lazy DOM, cloned from DemoLimitPopup) */
  isVisible: boolean;

  /** True while the cancel mutation is in-flight; disables both buttons + shows spinner */
  isSubmitting: boolean;

  /**
   * Set to a generic message when the cancel API call fails, so the student
   * can retry. Do NOT expose raw backend error text (security: no internal detail leakage).
   */
  errorMessage?: string | null;

  /** Called when the student confirms ending the session and reporting the issue */
  onConfirm: () => void;

  /** Called when the student dismisses without cancelling (Escape, button, or overlay click) */
  onDismiss: () => void;
}

/**
 * ReportIssueDialog — full-screen accessible modal for the Report Issue flow.
 *
 * Features (cloned from DemoLimitPopup):
 *   - role="dialog" aria-modal="true" with aria-label
 *   - Auto-focus on the confirm button when opened (via useEffect + 50ms delay)
 *   - Escape key calls onDismiss
 *   - Tab focus-trap cycles between confirm and dismiss buttons
 *   - Returns null when isVisible=false (no DOM cost until opened)
 *
 * AC-4 exact copy: "Your current session will be cancelled. No Otter Coins will be used."
 * CCM-03: No coin numbers, no wallet data, no balance shown.
 */
export function ReportIssueDialog({
  isVisible,
  isSubmitting,
  errorMessage,
  onConfirm,
  onDismiss,
}: ReportIssueDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the confirm button when dialog opens (cloned from DemoLimitPopup lines 98-106)
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Keyboard handling: Escape → dismiss; Tab → focus-trap between two buttons
  // Cloned from DemoLimitPopup lines 117-129, extended to two-button focus-trap
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
      // Focus-trap: cycle Tab between confirm and dismiss buttons
      if (e.key === 'Tab') {
        e.preventDefault();
        if (document.activeElement === confirmButtonRef.current) {
          dismissButtonRef.current?.focus();
        } else {
          confirmButtonRef.current?.focus();
        }
      }
    },
    [onDismiss]
  );

  // Don't render if not visible — no dialog DOM until opened (cloned from DemoLimitPopup line 132)
  if (!isVisible) {
    return null;
  }

  return (
    /* Overlay — same pattern as DemoLimitPopup lines 138-144 */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label="Report a problem"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Centered card — cloned from DemoLimitPopup line 147 */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-sm mx-4">
        {/* Warning icon */}
        <div className="text-3xl md:text-4xl mb-3" aria-hidden="true">
          ⚠️
        </div>

        {/* Heading */}
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Report a problem?</h2>

        {/* AC-4 EXACT copy — CCM-03: only permitted money reference; no coin numbers */}
        <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4">
          Your current session will be cancelled. No Otter Coins will be used.
        </p>

        {/* Inline error — shown when cancel API call fails; generic text (no leakage) */}
        {errorMessage && (
          <p className="text-sm text-red-600 mb-4" role="alert">
            {errorMessage}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          {/* Confirm: End session */}
          <Button
            ref={confirmButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            disabled={isSubmitting}
            className="w-full"
            variant="destructive"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Ending...
              </>
            ) : (
              'End session'
            )}
          </Button>

          {/* Dismiss: Keep trying */}
          <Button
            ref={dismissButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            disabled={isSubmitting}
            variant="outline"
            className="w-full"
          >
            Keep trying
          </Button>
        </div>
      </div>
    </div>
  );
}
